import { StyleColors } from '@gsc/types'
import { Color } from '../../helpers/color'
import { mapValues } from '../../../shared/collection'

export interface ColorVariables extends StyleColors {
  buttonHover: string
  secondaryButtonHover: string
  inputPlaceholder: string
}

function createTextColor(bg: Color): Color {
  return Color.contrast(bg, Color.fromString('#4c4c4c'), Color.fromString('#ffffff'))
}

function createDefaultColors(background = '#ffffff', text?: string): StyleColors {
  const bgColor = Color.fromString(background)
  const textColor = text ? Color.fromString(text) : createTextColor(bgColor)
  const buttonBgColor = Color.contrast(bgColor, bgColor.darken(20), bgColor.lighten(20))
  const secondaryButtonBgColor = Color.contrast(bgColor, bgColor.darken(10), bgColor.lighten(10))
  const normalButtonBgColor = Color.contrast(bgColor, textColor.fade(5), textColor.fade(15))
  const inputBgColor = Color.contrast(bgColor, textColor.fade(5), textColor.fade(15))

  const colors = {
    background: bgColor,
    text: textColor,
    headerText: textColor,
    button: buttonBgColor,
    buttonText: createTextColor(buttonBgColor),
    input: inputBgColor,
    inputText: textColor,
    secondaryButton: secondaryButtonBgColor,
    secondaryButtonText: textColor,
    normalButton: normalButtonBgColor,
    normalButtonText: textColor,
  }

  return mapValues(colors, (c: Color) => c.toRgbaString())
}

function resolveButtonHoverBg(background: string): string {
  const bgColor = Color.fromString(background)
  return Color.contrast(bgColor, bgColor.darken(5), bgColor.lighten(5)).toRgbaString()
}

function resolveInputPlaceholderColor(inputText: string): string {
  return Color.fromString(inputText).fadeout(50, true).toRgbaString()
}

export function resolveColorVariables(partialColors: Partial<StyleColors> = {}): ColorVariables {
  const colors: StyleColors = {
    ...createDefaultColors(partialColors.background, partialColors.text),
    ...mapValues(partialColors as StyleColors, c => Color.fromString(c).toRgbaString()),
    // ensures that all colors are in RGBA format (required for svg images with inlined colors in)
    // see './icons.ts'
  }

  return {
    ...colors,
    buttonHover: resolveButtonHoverBg(colors.button),
    secondaryButtonHover: resolveButtonHoverBg(colors.secondaryButton),
    inputPlaceholder: resolveInputPlaceholderColor(colors.inputText),
  }
}
